import { ReactNode } from "react";
import { Box, Container, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  colorLandingInk,
  colorLandingLine,
  colorLandingSurface,
  colorLandingInkPressed,
  colorLandingOnDarkMuted,
} from "src/design/tokens";
import { SectionHeading } from "./components/SectionHeading";
import { FeatureCheckList } from "./components/FeatureCheckList";
import { landingLinks } from "./landingLinks";
import { AppleIcon, PlayIcon } from "./components/StoreIcons";
import appMapScreen from "src/assets/landing/app-map-screen.jpg";

const CHECK_KEYS = ["discover", "deals", "events", "favorites"];

interface StoreButtonProps {
  href: string;
  icon: ReactNode;
  kicker: string;
  name: string;
}

const StoreButton = ({ href, icon, kicker, name }: StoreButtonProps) => (
  <Box
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      backgroundColor: colorLandingInk,
      color: colorLandingSurface,
      borderRadius: "13px",
      px: 2.25,
      py: 1.25,
      textDecoration: "none",
      "&:hover": { backgroundColor: colorLandingInkPressed },
    }}
  >
    {icon}
    <Box>
      <Typography sx={{ fontSize: 9, color: colorLandingOnDarkMuted, lineHeight: 1.1 }}>
        {kicker}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>
        {name}
      </Typography>
    </Box>
  </Box>
);

/**
 * The consumer-app argument: L'Addition is backed by a diner-facing app, so a
 * restaurant's deals and events land in front of an audience that already
 * exists. The phone shot deliberately bleeds into the section below.
 */
export const ClientAppSection = () => {
  const { t } = useTranslation();

  return (
    <Box
      component="section"
      id="app"
      sx={{
        pt: { xs: 5.5, md: 7.25 },
        pb: 0,
        borderTop: `1px solid ${colorLandingLine}`,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 5, md: 6.5 }} alignItems="flex-end">
          <Grid item xs={12} md={6} sx={{ alignSelf: "center" }}>
            <SectionHeading
              align="left"
              title={t("landingPage.clientApp.title")}
              subtitle={t("landingPage.clientApp.subtitle")}
            />
            <FeatureCheckList
              sx={{ mt: 2.75 }}
              fontSize={15.5}
              items={CHECK_KEYS.map((key) =>
                t(`landingPage.clientApp.checks.${key}`)
              )}
            />
            <Box sx={{ display: "flex", gap: 1.5, mt: 3.5, flexWrap: "wrap" }}>
              <StoreButton
                href={landingLinks.APP_STORE}
                icon={<AppleIcon />}
                kicker={t("landingPage.clientApp.downloadOn")}
                name={t("landingPage.clientApp.appStore")}
              />
              <StoreButton
                href={landingLinks.GOOGLE_PLAY}
                icon={<PlayIcon />}
                kicker={t("landingPage.clientApp.availableOn")}
                name={t("landingPage.clientApp.googlePlay")}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={appMapScreen}
              alt={t("landingPage.clientApp.screenshotAlt")}
              sx={{
                width: 262,
                maxWidth: "100%",
                height: "auto",
                display: "block",
                mx: "auto",
                verticalAlign: "bottom",
              }}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
